<template>
  <TableComp
    v-if="sites"
    :name="$t('global.dev-environments')"
    :headers="headers"
    :status="sites.status"
    :pagination="sites.pagination"
    :sort="sites.sort"
    :number-of-items="sites.list.length"
    empty-image="sites"
    :empty-bold-message="$t('global.no-dev-sites-bold-message')"
    :empty-message="$t('global.no-dev-sites-message')"
    v-on:update:list="updateList"
    :header-actions="actions"
    :empty-actions="actions"
  >
    <!-- desktop table layout -->
    <template #desktop>
      <tr v-for="site in sites.list" v-bind:key="site.id">
        <!-- site domain column -->
        <td>
          <div v-if="sites.status.isLoading" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="sites.status.isErrored" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <div v-else>
            <router-link v-if="site.isStable" :to="getRouterLink(site)">
              {{ site.domain }}
            </router-link>
            <span v-else>{{ site.domain }}</span>
          </div>
        </td>

        <!-- temp domain column -->
        <td class="text-center">
          <div v-if="sites.status.isLoading" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="sites.status.isErrored" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else>
            {{ site.environmentType }}
          </span>
        </td>

        <!-- ip address column -->
        <td class="text-center">
          <div v-if="sites.status.isLoading" class="d-flex justify-content-center">
            <LoadingState :size="iconSize" />
          </div>
          <div v-else-if="sites.status.isErrored" class="d-flex justify-content-center">
            <ItemErrorState />
          </div>
          <span v-else-if="site.ip">
            <span class="mr-2">{{ site.ip }}</span>
            <CopyToClipboard :text="site.ip" />
          </span>
        </td>

        <!-- three dots menu column -->
        <td class="text-end actions">
          <div v-if="site.isStable" class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(site)" />
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <LoadingState :size="iconSize" />
            </div>
            <div class="text-capitalize">{{ site.state }}...</div>
          </div>
        </td>
      </tr>
    </template>

    <!-- mobile table layout -->
    <template #mobile>
      <TableMobileRow v-for="site in sites.list" v-bind:key="site.id">
        <template #header>
          <div class="d-flex align-items-center">
            <!-- site domain on the left -->
            <div class="flex-fill">
              <router-link :to="getRouterLink(site)">
                {{ site.domain }}
              </router-link>
            </div>

            <!-- three dot menu on the left -->
            <div>
              <div v-if="site.isStable" class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(site)" />
              </div>
              <div v-else class="d-flex justify-content-center align-items-center">
                <div class="mr-2">
                  <LoadingState :size="iconSize" />
                </div>
                <div class="text-capitalize">{{ site.state }}...</div>
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <!-- site domain -->
          <div class="row">
            <div class="col">{{ $t("global.domain") }}:</div>
            <div class="col">
              <LoadingState v-if="sites.status.isLoading" :size="iconSize" />
              <item-error-state v-else-if="sites.status.isErrored" />
              <router-link v-else :to="getRouterLink(site)">
                {{ site.domain }}
              </router-link>
            </div>
          </div>

          <!-- temporary site domain -->
          <div class="row">
            <div class="col">{{ $t("global.type") }}:</div>
            <div class="col">
              <LoadingState v-if="sites.status.isLoading" :size="iconSize" />
              <item-error-state v-else-if="sites.status.isErrored" />
              <span v-else>{{ site.environmentType }}</span>
            </div>
          </div>

          <!-- site ip -->
          <div class="row">
            <div class="col">{{ $t("global.ip-address") }}:</div>
            <div class="col">
              <LoadingState v-if="sites.status.isLoading" :size="iconSize" />
              <item-error-state v-else-if="sites.status.isErrored" />
              <div v-else-if="site.ip">
                <span class="mr-2">{{ site.ip }}</span>
                <CopyToClipboard :text="site.ip" />
              </div>
            </div>
          </div>

          <!-- site status -->
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col">
              <LoadingState v-if="sites.status.isLoading" :size="iconSize" />
              <item-error-state v-else-if="sites.status.isErrored" />
              <span v-else class="text-capitalize">{{ site.state }}</span>
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import constants from "@/utils/constants";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import DevSiteAddModal from "@/components/smart/modals/DevSiteAdd";
import DevSiteDeleteModal from "@/components/smart/modals/DevSiteDelete";
import DevStagingSelectionModal from "@/components/smart/modals/DevStagingSelection";
import ItemErrorState from "@/components/display/ItemErrorState";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";

export default {
  name: "DevSitesTable",
  props: {
    sites: Object,
    site: Object,
    plan: Object,
  },
  components: {
    ItemErrorState,
    LoadingState,
    TableComp,
    TableMobileRow,
    ThreeDotsMenu,
    CopyToClipboard,
  },
  setup(props, { root }) {
    // table column headers for desktop version of site list table
    const headers = [
      new TableHeader(root.$t("global.domain"), null, "domain"),
      new TableHeader(root.$t("global.type"), "justify-content-center"),
      new TableHeader(root.$t("global.ip-address"), "justify-content-center"),
      new TableHeader(),
    ];

    const createSite = () => {
      // check if there's a staging site in the list
      const stagingSite = props.sites.list.find(
        ({ environmentType }) => environmentType === "staging"
      );
      // if a staging site exists, or if the plan is a legacy plan (LW'y), only allow them to create dev sites from here on
      if (stagingSite || props.plan.isTShirt) {
        store.dispatch("modals/open", {
          template: DevSiteAddModal,
          data: { site: props.site, plan: props.plan, environmentType: "development" },
        });
      } else {
        // else let them choose between dev or staging
        store.dispatch("modals/open", {
          template: DevStagingSelectionModal,
          data: { site: props.site, plan: props.plan },
        });
      }
    };

    const deleteSite = (site) => {
      store.dispatch("modals/open", {
        template: DevSiteDeleteModal,
        data: { domain: site.domain, environmentType: site.environmentType, planId: site.plan.id },
      });
    };

    // menu items for three dots menu on the right
    const menuItems = (site) => {
      return [
        // go to site dashboard
        new NavLink(root.$t("global.site-dashboard"), null, () => {
          root.$router.push({
            name: "plan-site-dev-detail",
            params: { devSiteId: site.id },
          });
        }),
        new NavLink(root.$t("global.delete-site"), null, () => deleteSite(site), null, "danger"),
      ];
    };

    const actions = [
      new NavLink(root.$t("global.add-dev-environment"), "add", createSite, false, null, "epsilon"),
    ];

    const getRouterLink = (site) => ({
      name: "plan-site-dev-detail",
      params: {
        devSiteId: site.id,
      },
    });

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("devSites/getList", {
        parentSiteId: props.site.id,
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    const iconSize = constants.TABLE_ICON_SIZE;

    return {
      headers,
      actions,
      menuItems,
      updateList,
      getRouterLink,
      iconSize,
    };
  },
};
</script>
